import { ReactTFC } from "../../../../../utils/fp/missing-ts-parts";
import { useStore } from "../../../../../store/hooks/use-store";
import { getIsoCodeWithIntFallback } from "@swo/shared";
import { PaypalButtonStatus } from "@swo/storefront";
import React, { useEffect, useState } from "react";
import { PayPalElementProps } from "@adyen/adyen-web/dist/types/components/PayPal/types";
import { useConfiguredAdyenInstance } from "../../../checkout-page-layout/CheckoutComponent/adyen/use-adyen-instance";
import UIElement from "@adyen/adyen-web/dist/types/components/UIElement";
import { usePageContext } from "../../../../../global/page-context";
import { useOpaqueValue } from "../../../../../hooks/use-opaque-value";
import cn from "classnames";
import { css } from "@emotion/react";
import { CheckoutHeadline } from "../../../checkout-page-layout/CheckoutComponent/shared/CheckoutHeadline";
import { SmallLoadingSpinner } from "../../../../../routes/modal-loading-spinner";
import { FONTS, fontSize } from "../../../../../styles/fonts";
import type { TranslatedValue } from "@swo/storefront";
import { T } from "../../../../../components/T";
const containerId = "paypal-button-container";

export const PaypalPayButton: ReactTFC<{
  actionData: PaypalButtonStatus["data"];
  merchantId: string;
  amount: {
    currency: string;
    value: number;
  };
  onAdditionalData: (additionalData: { details: unknown; paymentData: string }) => void;
  headline: string;
  paragraph: TranslatedValue;
  intent: "capture" | "authorize" | undefined;
}> = props => {
  const { state } = useStore();
  const pageCtx = usePageContext();
  const { clientKey, environment } = state.config.adyen;
  const [init, setInit] = useState(false);
  const initRef = useOpaqueValue(init);

  const adyenInstance = useConfiguredAdyenInstance(
    (state: any, component: UIElement<PayPalElementProps>) => {
      // console.log("additional data", state, component);
      props.onAdditionalData(state.data);
    },
    (state, component) => {
      console.log(state, component);
    },
  );

  useEffect(() => {
    if (!adyenInstance) {
      return;
    }

    const configuration: PayPalElementProps = {
      onInit: (data, actions) => {
        // onInit is called when the button first renders.
        // console.log("INIT", data, actions);

        //@ts-expect-error
        actions.enable();

        setInit(true);
      },
      onClick: () => {
        // onClick is called when the button is clicked.
        // console.log("onclick");
      },
      onError: state => {
        // console.log("error", state);
      },

      onCancel: state => {
        // console.log("cancel", state);
      },
      onSubmit: (state, component: UIElement<PayPalElementProps>) => {
        if (!initRef.current) {
          return;
        }

        // console.log(state, component);

        if (state.isValid) {
          setTimeout(() => {
            component.handleAction(props.actionData);
          }, 500);
        } else if (state.data.details && state.data.paymentData) {
          // console.log("action", state);
          component.handleAction(state.data);
        }
      },
      environment: environment,
      countryCode: getIsoCodeWithIntFallback(pageCtx.pageBase.locale.country), // Only needed for test. When live, this is retrieved automatically.
      amount: props.amount,
      blockPayPalCreditButton: true,
      blockPayPalPayLaterButton: true,
      showPayButton: true,
      configuration: {
        merchantId: props.merchantId,
        intent: props.intent,
      },
    };

    const instance = adyenInstance.underlying;
    const i = instance.create("paypal", configuration);

    const el = i.mount(`#${containerId}`);

    return () => {
      if (initRef.current) {
        el.remove();
      }
    };
  }, [adyenInstance]);

  return (
    <div>
      {!init && (
        <div
          css={css`
            display: flex;
            justify-content: center;
          `}
        >
          <SmallLoadingSpinner color={"green"} />
        </div>
      )}

      <div
        className={cn({ hidden: !init })}
        css={css`
          &.hidden {
            display: none;
          }
        `}
      >
        <div
          css={css`
            margin-bottom: 40px;
          `}
        >
          <CheckoutHeadline
            css={css`
              margin-top: 0;
            `}
          >
            {props.headline}
          </CheckoutHeadline>
          <p
            css={[
              FONTS.SwarovskiFutura300,
              fontSize.x3,
              css`
                display: block;
                text-align: center;
                margin-bottom: 12px;
              `,
            ]}
          >
            <T>{props.paragraph}</T>
          </p>
        </div>

        <div
          id={containerId}
          css={css`
            display: flex;
            justify-content: center;
          `}
        />
      </div>
    </div>
  );
};
